import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Unsaved form work kept on this device until it is saved or the day ends.
const PREFIX = 'dl-draft:';
const today = () => new Date().toLocaleDateString('en-CA');

const sweep = () => {
  try {
    Object.keys(localStorage).filter((k) => k.startsWith(PREFIX)).forEach((k) => {
      try { if (JSON.parse(localStorage.getItem(k)).day !== today()) localStorage.removeItem(k); } catch { localStorage.removeItem(k); }
    });
  } catch { /* storage unavailable */ }
};

/**
 * key: e.g. 'estimate:new' or `invoice:${id}`. ready: the form holds its loaded/initial values.
 * Returns { offer, restore, discard, clear, touch }. Call touch on user input, clear after a successful save.
 */
export function useDeviceDraft(key, form, setForm, ready) {
  const { user } = useAuth();
  const full = `${PREFIX}${user?.tenant_id || ''}:${user?.id || user?.email || ''}:${key}`;
  const [offer, setOffer] = useState(null);
  const base = useRef(null);
  const touched = useRef(false);
  const timer = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    base.current = null; touched.current = false; setOffer(null);
  }, [full]);

  useEffect(() => {
    if (!ready || base.current !== null) return;
    sweep();
    base.current = JSON.stringify(form);
    try {
      const d = JSON.parse(localStorage.getItem(full) || 'null');
      if (d && d.day === today() && JSON.stringify(d.data) !== base.current) setOffer(d);
      else if (d) localStorage.removeItem(full);
    } catch { /* ignore */ }
  }, [ready, form, full]);

  useEffect(() => {
    if (!ready || base.current === null || offer || !touched.current) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try {
        const s = JSON.stringify(form);
        if (s === base.current) localStorage.removeItem(full);
        else localStorage.setItem(full, JSON.stringify({ data: form, at: new Date().toISOString(), day: today() }));
      } catch { /* quota or private mode */ }
    }, 500);
    return () => clearTimeout(timer.current);
  }, [form, ready, offer, full]);

  const restore = useCallback(() => { if (offer) { setForm(offer.data); touched.current = true; } setOffer(null); }, [offer, setForm]);
  const discard = useCallback(() => {
    clearTimeout(timer.current);
    try { localStorage.removeItem(full); } catch { /* ignore */ }
    // Auto-filled defaults (e.g. a validity date) are not the user's work.
    touched.current = false; base.current = JSON.stringify(formRef.current);
    setOffer(null);
  }, [full]);
  const clear = useCallback(() => {
    clearTimeout(timer.current);
    try { localStorage.removeItem(full); } catch { /* ignore */ }
    touched.current = false; base.current = null;
  }, [full]);
  const touch = useCallback((e) => {
    if (e?.target?.closest?.('[data-draft-bar]')) return;
    touched.current = true;
  }, []);
  return { offer, restore, discard, clear, touch };
}
